import React, { useMemo, useState } from 'react'
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Link,
  Paper,
  RadioGroup,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import moment from 'moment'
import i18next from 'i18next'
import Notiflix from 'notiflix'
import styles from './Payment.module.scss'
import { Title } from '../../Title'
import { CustomRadio } from '../../../../../../shared-components/checkbox/CustomRadio'
import { CustomTextfield } from '../../../../../../shared-components/textfield/CustomTextField'
import { TabValue } from '../../../AccountDetailsSeller'
import { RootState } from '../../../../../../../redux/reducers/rootReducer'
import instance from '../../../../../../../providers/axios.config'
import { ROUTES, WAITING } from '../../../../../../../utils/constants'
import { daysBetweenDates, formatDollar } from '../../../../../../../utils/helpers'
import { useCustomNavigation } from '../../../../../../../hooks/useCustomNavigate'
import { IUser } from '../../../../../../../utils/types'

/* -------------------------------------------------------------------------- */
/* Constants & types                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Exact list of payout methods the backend accepts — mirrors the
 * PayoutMethods enum on layerstock-api. Keep in sync with the
 * backend rule `Rule::in(PayoutMethods::all())`.
 */
const PAYOUT_METHODS = ['Revolut', 'Skrill', 'PayPal', 'Payoneer'] as const
type PayoutMethodId = typeof PAYOUT_METHODS[number]

const PAYOUT_METHOD_OPTIONS: ReadonlyArray<{ id: PayoutMethodId; label: string }> = [
  { id: 'Revolut',  label: 'Revolut' },
  { id: 'Skrill',   label: 'Skrill' },
  { id: 'PayPal',   label: 'PayPal' },
  { id: 'Payoneer', label: 'Payoneer' },
]

/**
 * Cooldown (in days) between two payout requests. Matches the
 * SellerCanRequestPayoutRule window on the backend. Displayed to
 * the seller in the "please wait X more days" banner.
 */
const PAYOUT_COOLDOWN_DAYS = 15

interface PaymentProps {
  filterValue?: string
  onChangeFilterValue?: (key: string) => void
  onChangeSections?: (key: TabValue) => void
}

interface FormErrors {
  amount: string
  email: string
  confirmEmail: string
}

const EMAIL_REGEX = /^\S+@\S+\.\S+$/

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The user row stores `minPayout` in CENTS (per backend
 * UserResource). Convert here once; every downstream comparison
 * and display runs in dollars.
 */
const minPayoutDollars = (userData: IUser | null | undefined): number => {
  if (!userData) return 0
  return (userData.minPayout ?? 0) / 100
}

/**
 * Days remaining until the seller can request another payout.
 * Zero (or negative) means the cooldown has already expired.
 */
const cooldownDaysRemaining = (userData: IUser | null | undefined): number => {
  const last = userData?.lastSellerPayout
  if (!last?.created_at) return 0
  const elapsed = daysBetweenDates(moment(), moment(last.created_at))
  return Math.max(0, PAYOUT_COOLDOWN_DAYS - elapsed)
}

/**
 * Compose the top banner shown above the payout form. Covers the
 * three "can't request" reasons spelled out by the backend: waiting
 * tax form, in-flight payout, and missing-tax-form.
 */
const bannerMessage = (userData: IUser | null | undefined): string | null => {
  if (!userData) return null
  if (userData.lastSellerTaxForm?.status === WAITING) {
    return i18next.t('tax_form_waiting_approve')
  }
  if (userData.lastSellerPayout?.status === WAITING) {
    return i18next.t('payment_waiting', { days: cooldownDaysRemaining(userData) })
  }
  if (!userData.lastSellerPayout && !userData.lastSellerTaxForm) {
    return i18next.t('add_tax_form_message')
  }
  return null
}

/**
 * Reads the saved payout destination the seller configured in
 * AccountSettings, matching the selected method. Used to auto-fill
 * the "payout email" input when the seller picks a method.
 */
const savedDestinationFor = (
  userData: IUser | null | undefined,
  method: PayoutMethodId
): string => {
  if (!userData) return ''
  switch (method) {
    case 'Revolut':  return userData.payoutRevolut  ?? ''
    case 'Skrill':   return userData.payoutSkrill   ?? ''
    case 'PayPal':   return userData.payoutPaypal   ?? ''
    case 'Payoneer': return userData.payoutPayoneer ?? ''
  }
}

/**
 * Page header — title + description are driven by the nested-tab
 * filter value passed down from the dashboard layout.
 */
const pageTitleAndDescription = (
  filterValue: string | null | undefined,
  t: ReturnType<typeof useTranslation>['t']
): { title: string; description: string } => {
  switch (filterValue) {
    case '0':
      return { title: t('payment_history'), description: '' }
    default:
      return { title: t('payment_method'), description: '' }
  }
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Seller payout request form.
 *
 * Pre-conditions (mirrors the backend rule set):
 *   1. An approved, non-expired tax form (W-9 / W-8BEN / W-8BEN-E)
 *      must be on file for the current fiscal year.
 *   2. The seller's available balance (`sellerEarningsUnpaid`) must
 *      be ≥ `minPayout` (cents) / 100.
 *   3. No payout created in the last 15 days.
 *
 * When any pre-condition is NOT met, the form inputs are disabled
 * and a banner explains why.
 *
 * Redux-based auto-fill: picking a method pre-populates the "payout
 * email" field with the identifier the seller already saved on their
 * AccountSettings page for that method (PayPal email, Revolut tag,
 * etc.). The seller can still override it inline.
 */
export const Payment = (props: PaymentProps): JSX.Element => {
  const { t } = useTranslation()
  const { filterValue } = props
  const userData = useSelector((state: RootState) => state.user.usersData) as IUser | null

  const [method, setMethod] = useState<PayoutMethodId>('Revolut')
  const [requestPayment, setRequestPayment] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [confirmEmail, setConfirmEmail] = useState<string>('')
  const [errors, setErrors] = useState<FormErrors>({ amount: '', email: '', confirmEmail: '' })
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const customNavigate = useCustomNavigation()

  /* ─── Derived values ───────────────────────────────────────────── */

  const minPayout = useMemo(() => minPayoutDollars(userData), [userData])
  const availableBalance = Number(userData?.sellerEarningsUnpaid ?? 0)

  const canNotRequestPayment = useMemo<boolean>(() => {
    if (!userData?.lastSellerTaxForm) return true
    if (userData.lastSellerTaxForm.status === WAITING) return true
    if (availableBalance < minPayout) return true
    if (userData.lastSellerPayout
        && daysBetweenDates(moment(), moment(userData.lastSellerPayout.created_at)) < PAYOUT_COOLDOWN_DAYS) {
      return true
    }
    return false
  }, [userData, availableBalance, minPayout])

  const banner = bannerMessage(userData)
  const headerText = pageTitleAndDescription(filterValue, t)

  /* ─── Handlers ────────────────────────────────────────────────── */

  const handleChangePayment = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    const next = ev.target.value as PayoutMethodId
    setMethod(next)
    // Auto-fill email with whatever the seller saved for this method
    // in AccountSettings. They can override inline.
    setEmail(savedDestinationFor(userData, next))
  }

  const handleChangeRequestPayment = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    const value = ev.target.value
    setRequestPayment(value)

    const amount = Number(value)
    if (!value.trim() || Number.isNaN(amount) || amount <= 0) {
      setErrors((prev) => ({ ...prev, amount: t('invalid_amount') }))
      return
    }
    if (amount > availableBalance) {
      setErrors((prev) => ({
        ...prev,
        amount: t('amount_above_balance', { max: formatDollar(availableBalance) }),
      }))
      return
    }
    if (amount < minPayout) {
      setErrors((prev) => ({
        ...prev,
        amount: t('amount_below_minimum', { min: formatDollar(minPayout) }),
      }))
      return
    }
    setErrors((prev) => ({ ...prev, amount: '' }))
  }

  const handleChangeEmail = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(ev.target.value)
    if (errors.email) setErrors((prev) => ({ ...prev, email: '' }))
  }

  const handleChangeConfirmEmail = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    setConfirmEmail(ev.target.value)
    if (errors.confirmEmail) setErrors((prev) => ({ ...prev, confirmEmail: '' }))
  }

  const validateBeforeSubmit = (): boolean => {
    const next: FormErrors = { amount: '', email: '', confirmEmail: '' }

    if (!email.trim() || !EMAIL_REGEX.test(email.trim())) {
      next.email = t('invalid_email')
    }
    if (!confirmEmail.trim() || !EMAIL_REGEX.test(confirmEmail.trim())) {
      next.confirmEmail = t('invalid_email')
    }
    if (email.trim() && confirmEmail.trim() && email.trim() !== confirmEmail.trim()) {
      next.confirmEmail = t('emails_do_not_match')
    }

    const amount = Number(requestPayment)
    if (!requestPayment.trim() || Number.isNaN(amount) || amount <= 0) {
      next.amount = t('invalid_amount')
    } else if (amount > availableBalance) {
      next.amount = t('amount_above_balance', { max: formatDollar(availableBalance) })
    } else if (amount < minPayout) {
      next.amount = t('amount_below_minimum', { min: formatDollar(minPayout) })
    }

    setErrors(next)
    return next.amount === '' && next.email === '' && next.confirmEmail === ''
  }

  const handleSaveRequestPayment = async (): Promise<void> => {
    if (!validateBeforeSubmit()) return

    setIsLoading(true)
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })

    try {
      // Backend expects lowercase method strings matching the PayoutMethods
      // enum (paypal, payoneer, skrill, revolut). `method_info` carries
      // the account identifier the seller supplied (email / tag / IBAN).
      const payload = {
        amount:      Number(requestPayment),
        method:      method.toLowerCase(),
        method_info: email.trim(),
        email:       email.trim(),
      }

      await instance.post('seller/tax-center/request-payment', payload)

      Notiflix.Report.success(
        t('payout_request_submitted'),
        t('payout_request_check_email'),
        'OK'
      )

      setRequestPayment('')
      setEmail('')
      setConfirmEmail('')
    } catch (error: unknown) {
      const maybeAxios = error as {
        response?: { data?: { message?: string; errors?: Record<string, string[]> } }
      }
      const message = maybeAxios?.response?.data?.message ?? t('payout_request_failed')
      Notiflix.Report.failure(t('error'), message, 'OK')
    } finally {
      setIsLoading(false)
      Notiflix.Loading.remove()
    }
  }

  const handleRedirectAccountPage = (): void => {
    customNavigate(`/${ROUTES.ACCOUNT}/${ROUTES.SETTINGS}`)
  }

  /* ─── Render ──────────────────────────────────────────────────── */

  return (
    <div>
      <Title
        title={filterValue ? headerText.title : ''}
        description={filterValue ? headerText.description : ''}
      />
      <div className={styles.content}>
        {banner && (
          <Paper className={styles.paperPayment}>
            <Typography fontSize={20} fontWeight="bold">{banner}</Typography>
          </Paper>
        )}
        <div className={styles.containerPayment}>
          <Typography>
            {t('minimum_payout_balance')}: {formatDollar(minPayout * 100)}
          </Typography>
          <Typography>
            {t('current_earning_balance')}: {formatDollar(availableBalance)}
          </Typography>
          <br />
          <Typography sx={{ display: 'flex' }}>
            <Link component="button" onClick={handleRedirectAccountPage}>
              {t('review_and_confirm')}
            </Link>
            &nbsp;{t('all_account_info')}
          </Typography>

          <div className={styles.requestAmount}>
            <Typography>{t('request_payment')}</Typography>
            <CustomTextfield
              size="small"
              type="number"
              disabled={canNotRequestPayment}
              variant="outlined"
              className={styles.textAmount}
              error={Boolean(errors.amount)}
              value={requestPayment}
              onChange={handleChangeRequestPayment}
              helperText={errors.amount}
              label={t('amount')}
            />
          </div>

          <FormControl className={styles.labelStyleTax}>
            <Typography className={styles.labelStyleTax}>{t('payout_method')}</Typography>
            <RadioGroup
              aria-labelledby="payout-method-group-label"
              name="payout-method"
              onChange={handleChangePayment}
              value={method}
            >
              {PAYOUT_METHOD_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.id}
                  disabled={canNotRequestPayment}
                  value={option.id}
                  control={<CustomRadio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Typography marginTop={4}>{t('payout_email')}</Typography>
          <div className={styles.contentEmails}>
            <CustomTextfield
              className={styles.textfield}
              size="small"
              disabled={canNotRequestPayment}
              variant="outlined"
              value={email}
              onChange={handleChangeEmail}
              error={Boolean(errors.email)}
              helperText={errors.email}
              label={t('email')}
            />
            <CustomTextfield
              className={styles.textfield}
              size="small"
              disabled={canNotRequestPayment}
              variant="outlined"
              value={confirmEmail}
              onChange={handleChangeConfirmEmail}
              error={Boolean(errors.confirmEmail)}
              helperText={errors.confirmEmail}
              label={t('confirm_email_address')}
            />
          </div>
          <br />
          <Button
            variant="contained"
            onClick={handleSaveRequestPayment}
            className={styles.button}
            disabled={
              isLoading
              || Boolean(errors.amount)
              || canNotRequestPayment
            }
          >
            {isLoading ? <CircularProgress size={20} /> : t('request_payment')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Payment
